import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import {
  PlusIcon,
  CubeIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { batchesAPI } from '../services/api';
import {
  LoadingSpinner,
  EmptyState,
  StatCard,
  Badge,
  Modal,
  SearchInput,
  FilterButton,
  ConfirmDialog,
  DataTable,
  Pagination,
} from '../components/common';
import { formatDate, formatNumber } from '../utils/formatters';

export default function BatchesPage() {
  const queryClient = useQueryClient();
  const { register, handleSubmit, reset, formState: { errors } } = useForm();

  // State
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [confirmAction, setConfirmAction] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Fetch batches
  const { data: batchesData, isLoading } = useQuery({
    queryKey: ['batches', searchTerm, statusFilter, currentPage, pageSize],
    queryFn: async () => {
      const params = {
        page: currentPage,
        limit: pageSize,
      };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (searchTerm) params.search = searchTerm;
      const response = await batchesAPI.getAll(params);
      return response.data;
    },
  });

  const batches = batchesData?.data || [];
  const pagination = batchesData?.pagination || { total: 0, pages: 1 };
  console.log(batches);
  
  // Create batch mutation
  const createMutation = useMutation({
    mutationFn: (data) => batchesAPI.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
      toast.success('Batch created successfully!');
      setIsCreateModalOpen(false);
      reset();
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to create batch');
    },
  });

  // Activate batch mutation
  const activateMutation = useMutation({
    mutationFn: (id) => batchesAPI.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
      toast.success('Batch activated successfully!');
      setConfirmAction(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to activate batch');
    },
  });

  // Close batch mutation
  const closeMutation = useMutation({
    mutationFn: (id) => batchesAPI.close(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
      toast.success('Batch closed successfully!');
      setConfirmAction(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to close batch');
    },
  });

  const onSubmit = (data) => {
    createMutation.mutate({
      ...data,
      total_slots: parseInt(data.total_slots),
      total_days: parseInt(data.total_days),
    });
  };

  const handleActivate = (batch) => {
    setConfirmAction({
      type: 'activate',
      batch,
      title: 'Activate Batch',
      message: `Are you sure you want to activate "${batch.name || batch.batch_number}"? This will make it available for member enrollment.`,
    });
  };

  const handleClose = (batch) => {
    setConfirmAction({
      type: 'close',
      batch,
      title: 'Close Batch',
      message: `Are you sure you want to close "${batch.name || batch.batch_number}"? This action cannot be undone.`,
    });
  };

  const handleDelete = (batch) => {
    setConfirmAction({
      type: 'delete',
      batch,
      title: 'Delete Batch',
      message: `Are you sure you want to delete "${batch.name || batch.batch_number}"? This action cannot be undone.`,
    });
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => batchesAPI.deleteBatch(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['batches']);
      toast.success('Batch deleted successfully!');
      setConfirmAction(null);
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || 'Failed to delete batch');
      setConfirmAction(null);
    },
  });

  const confirmActionHandler = () => {
    if (confirmAction.type === 'activate') {
      activateMutation.mutate(confirmAction.batch.id);
    } else if (confirmAction.type === 'close') {
      closeMutation.mutate(confirmAction.batch.id);
    } else if (confirmAction.type === 'delete') {
      deleteMutation.mutate(confirmAction.batch.id);
    }
  };

  // Calculate stats
  const stats = {
    total: batches?.length || 0,
    active: batches?.filter(b => b.status === 'active').length || 0,
    open: batches?.filter(b => b.status === 'open').length || 0,
    completed: batches?.filter(b => b.status === 'completed').length || 0,
  };

  if (isLoading) {
    return <LoadingSpinner text="Loading batches..." />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Batches</h1>
          <p className="mt-2 text-sm text-gray-600">
            Manage lottery batches and track their progress
          </p>
        </div>
        <button
          onClick={() => setIsCreateModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          Create Batch
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Batches"
          value={stats.total}
          icon={CubeIcon}
          color="primary"
        />
        <StatCard
          title="Active"
          value={stats.active}
          icon={CheckCircleIcon}
          color="green"
        />
        <StatCard
          title="Open"
          value={stats.open}
          icon={CubeIcon}
          color="blue"
        />
        <StatCard
          title="Completed"
          value={stats.completed}
          icon={CheckCircleIcon}
          color="gray"
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <SearchInput
          value={searchTerm}
          onChange={setSearchTerm}
          onClear={() => setSearchTerm('')}
          placeholder="Search by batch number or name..."
          className="flex-1"
        />
        <div className="flex gap-2 flex-wrap">
          <FilterButton
            active={statusFilter === 'all'}
            onClick={() => setStatusFilter('all')}
          >
            All
          </FilterButton>
          <FilterButton
            active={statusFilter === 'open'}
            onClick={() => setStatusFilter('open')}
          >
            Open
          </FilterButton>
          <FilterButton
            active={statusFilter === 'active'}
            onClick={() => setStatusFilter('active')}
          >
            Active
          </FilterButton>
          <FilterButton
            active={statusFilter === 'full'}
            onClick={() => setStatusFilter('full')}
          >
            Full
          </FilterButton>
          <FilterButton
            active={statusFilter === 'completed'}
            onClick={() => setStatusFilter('completed')}
          >
            Completed
          </FilterButton>
        </div>
      </div>

      {/* Batches Grid */}
      {batches && batches.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {batches.map((batch) => (
            <div key={batch.id} className="card hover:shadow-lg transition-shadow">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {batch.name || `Batch ${batch.batch_number}`}
                  </h3>
                  <p className="text-sm text-gray-500">#{batch.batch_number}</p>
                </div>
                <Badge
                  variant={
                    batch.status === 'active' ? 'success' :
                    batch.status === 'open' ? 'info' :
                    batch.status === 'full' ? 'warning' :
                    batch.status === 'completed' ? 'default' :
                    'danger'
                  }
                >
                  {batch.status}
                </Badge>
              </div>

              {/* Stats */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Members</span>
                  <span className="font-semibold text-gray-900">
                    {formatNumber(batch.current_members || 0)} / {formatNumber(batch.total_slots)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Days</span>
                  <span className="font-semibold text-gray-900">
                    {batch.total_days} days
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Created</span>
                  <span className="text-gray-500">{formatDate(batch.created_at)}</span>
                </div>
              </div>

              {/* Progress Bar */}
              <div className="mb-4">
                <div className="flex justify-between text-xs text-gray-600 mb-1">
                  <span>Progress</span>
                  <span>
                    {Math.round(((batch.current_members || 0) / batch.total_slots) * 100)}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-primary-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(((batch.current_members || 0) / batch.total_slots) * 100, 100)}%`
                    }}
                  />
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-2">
                <Link
                  to={`/batches/${batch.id}`}
                  className="flex-1 btn-secondary flex items-center justify-center gap-2"
                >
                  <EyeIcon className="h-4 w-4" />
                  View
                </Link>
                {batch.status === 'open' && (
                  <>
                    <button
                      onClick={() => handleActivate(batch)}
                      className="flex-1 btn-primary flex items-center justify-center gap-2"
                    >
                      <CheckCircleIcon className="h-4 w-4" />
                      Activate
                    </button>
                    {(batch.current_members === 0 || !batch.current_members) && (
                      <button
                        onClick={() => handleDelete(batch)}
                        className="btn-danger flex items-center justify-center gap-2 px-3"
                        title="Delete Batch"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    )}
                  </>
                )}
                {(batch.status === 'active' || batch.status === 'full') && (
                  <button
                    onClick={() => handleClose(batch)}
                    className="flex-1 btn-danger flex items-center justify-center gap-2"
                  >
                    <XCircleIcon className="h-4 w-4" />
                    Close
                  </button>
                )}
              </div>
            </div>
          ))}
        {/* Pagination */}
        {batches && batches.length > 0 && (
          <Pagination
            currentPage={currentPage}
            totalPages={pagination.pages}
            totalItems={pagination.total}
            itemsPerPage={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={setPageSize}
            isLoading={isLoading}
          />
        )}
        </div>
      ) : (
        <EmptyState
          icon={CubeIcon}
          title="No batches found"
          description={searchTerm || statusFilter !== 'all' 
            ? "Try adjusting your filters" 
            : "Create your first batch to get started"
          }
          action={!searchTerm && statusFilter === 'all' ? () => setIsCreateModalOpen(true) : undefined}
          actionLabel="Create Batch"
        />
      )}

      {/* Create Batch Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => {
          setIsCreateModalOpen(false);
          reset();
        }}
        title="Create New Batch"
        size="md"
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Number *
            </label>
            <input
              {...register('batch_number', { required: 'Batch number is required' })}
              type="text"
              className="input-field"
              placeholder="BATCH001"
            />
            {errors.batch_number && (
              <p className="mt-1 text-sm text-red-600">{errors.batch_number.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Batch Name *
            </label>
            <input
              {...register('name', { required: 'Batch name is required' })}
              type="text"
              className="input-field"
              placeholder="Weekly Draw Batch 1"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Slots *
            </label>
            <input
              {...register('total_slots', {
                required: 'Total slots is required',
                min: { value: 1, message: 'Must be at least 1' }
              })}
              type="number"
              className="input-field"
              placeholder="100000"
            />
            {errors.total_slots && (
              <p className="mt-1 text-sm text-red-600">{errors.total_slots.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Total Days *
            </label>
            <input
              {...register('total_days', {
                required: 'Total days is required',
                min: { value: 1, message: 'Must be at least 1' }
              })}
              type="number"
              className="input-field"
              placeholder="30"
            />
            {errors.total_days && (
              <p className="mt-1 text-sm text-red-600">{errors.total_days.message}</p>
            )}
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={() => {
                setIsCreateModalOpen(false);
                reset();
              }}
              className="flex-1 btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isLoading}
              className="flex-1 btn-primary"
            >
              {createMutation.isLoading ? 'Creating...' : 'Create Batch'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Confirm Action Dialog */}
      {confirmAction && (
        <ConfirmDialog
          isOpen={true}
          onClose={() => setConfirmAction(null)}
          onConfirm={confirmActionHandler}
          title={confirmAction.title}
          message={confirmAction.message}
          confirmLabel={confirmAction.type === 'activate' ? 'Activate' : 'Close'}
          variant={confirmAction.type === 'activate' ? 'primary' : 'danger'}
          isLoading={activateMutation.isLoading || closeMutation.isLoading}
        />
      )}
    </div>
  );
}
